import React, { useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";

import LabTestCatalogService from "./LabTestCatalogService";
import { getTagsForSltCode } from "./SubtestTagsService";
import { CloseGlyph } from "./labIcons";

/**
 * Shared building blocks for the Digitise-manually experience — the
 * modal was retired; DigitiseManuallyCard now owns both the standalone
 * rail sheet and the transcript-mode inline card. This module keeps the
 * common bits that both paths consume: state factories, the TestCard /
 * SubTestCard components, the SearchSelect dropdown, and a couple of
 * SVG glyphs.
 *
 * Anything with LOCAL card-shape assumptions (modal outcome overlays,
 * batch save orchestration, ModalPortal usage) lived on the deleted
 * default export; if you're looking for it, it now lives inside
 * DigitiseManuallyCard.
 */

export const RANGE_OPTIONS = [
  { value: "none",     label: "None" },
  { value: "normal",   label: "Normal" },
  { value: "abnormal", label: "Abnormal" },
  { value: "high",     label: "High" },
  { value: "medium",   label: "Medium" },
  { value: "low",      label: "Low" },
];

// Placeholder unit list — reviewers can key in any string; this seeds the
// dropdown with the most common units. Real per-test unit hinting can
// land later once the catalog carries units.
// Unit list is fetched at runtime — see LabUnitsService (module-level
// cache, one call per page load; shared with the Correction dialog).

let _tid = 0;
export const nextId = () => {
  _tid += 1;
  return `t${_tid}`;
};

// Today in the browser's local timezone as `YYYY-MM-DD` — matches the
// native `<input type="date">` value format so `max=todayISO()` blocks
// tomorrow / future dates in the picker, and the save-side guard uses
// the same string for a direct comparison against the picked value.
export const todayISO = () => {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${dd}`;
};

export const blankSubTest = () => ({
  id: nextId(),
  sub: null, // full catalog subtest object once picked; null while empty
  loincName: "",     // populated via getSubTestOnSelect after pick
  loincLoading: false,
  value: "",
  unit: "",
  range: "",
  // Optional qualifier tag — the Tag dropdown is revealed only when
  // the picked sub-test's sltCode has an entry in the subtestTags
  // catalog (e.g. Morning / Evening BP, PP1 / PP2 / PP3 blood sugar).
  // When shown, the reviewer must pick one before Save is enabled.
  tag: "",
  // Optional free-form description (ZY-10340). Client-side gate requires
  // at least one of {value, description} before Save is enabled; the
  // backend enforces the same rule via ValidateValueOrDescription.
  description: "",
});

export const blankTest = () => ({
  id: nextId(),
  collapsed: false,
  labTest: null, // full catalog test object once picked; null while empty
  testDate: "",
  subTests: [blankSubTest()],
});


// ─── TestCard ───────────────────────────────────────────────────────────
export const TestCard = ({
  index,
  test,
  catalog,
  catalogLoading,
  unitOptions,
  pickedLtCodes,
  onUpdate,
  onPickLabTest,
  onRemove,
  onAddSub,
  onRemoveSub,
  onUpdateSub,
  disabled,
}) => {
  const { collapsed, labTest, testDate, subTests } = test;

  // Options for THIS card's Lab-test picker: all catalog tests EXCEPT
  // those already picked by another card (unless already picked by
  // THIS card — the reviewer needs to see their own pick).
  const labTestOptions = useMemo(() => catalog.filter((t) =>
    !pickedLtCodes.has(t.ltCode) || (labTest && labTest.ltCode === t.ltCode),
  ), [catalog, pickedLtCodes, labTest]);

  // Sub-tests for this card come from the picked Lab test. `null` upstream
  // means the test has no distinct sub-tests — we render the sub-test row
  // without a picker (LOINC + value/unit/range only, keyed against the
  // parent test's own LOINC).
  const hasSubTestCorpus = !!(labTest && Array.isArray(labTest.subtest) && labTest.subtest.length > 0);

  // Codes of sub-tests already picked in THIS card (used to filter each
  // row's dropdown so a sub-test can't appear twice within the same test).
  const pickedSltCodes = useMemo(() => {
    const s = new Set();
    subTests.forEach((r) => { if (r.sub && r.sub.sltCode) s.add(r.sub.sltCode); });
    return s;
  }, [subTests]);

  // Same guard as "Add another test" at the top level — if any sub-test
  // row is still empty (no pick), disable "Add sub test" so the reviewer
  // fills the blank row first.
  const hasBlankSubTest = useMemo(() => subTests.some((r) => !r.sub), [subTests]);
  // Corpus-exhaustion guard — once every sub-test in the parent's
  // corpus has been picked into a row, there's nothing left to add.
  // Disables "+ Add sub test" so the button stops offering an empty
  // row the reviewer can't populate. Only applies when the parent
  // has a corpus (parents without one write one row per test).
  const corpusSize = hasSubTestCorpus ? labTest.subtest.length : 0;
  const allSubTestsPicked = hasSubTestCorpus && pickedSltCodes.size >= corpusSize;

  const headerTitle = (labTest && labTest.testName) || `Test ${index}`;
  return (
    <div className={`li-dm-test${collapsed ? " is-collapsed" : ""}`}>
      <div className="li-dm-test-head">
        {/* Test-name label wraps into an outer .li-dm-tt-name-wrap
            because the inner .li-dm-test-name carries `overflow:
            hidden` for the ellipsis — the tooltip's absolutely-
            positioned ::after would otherwise get clipped by that
            same overflow. Outer wrap keeps `overflow: visible` so
            the popover reaches the surrounding card. */}
        <span
          className={`li-dm-tt-name-wrap${labTest ? " li-dm-tt" : ""}`}
          data-tt={labTest ? headerTitle : undefined}
        >
          <span className="li-dm-test-name">{headerTitle}</span>
        </span>
        <button
          type="button"
          className="li-dm-icon-btn"
          onClick={() => onUpdate({ collapsed: !collapsed })}
          aria-label={collapsed ? "Expand" : "Collapse"}
          title={collapsed ? "Expand" : "Collapse"}
          disabled={disabled}
        >
          <ChevronIcon direction={collapsed ? "down" : "up"} />
        </button>
        <button
          type="button"
          className="li-dm-icon-btn"
          onClick={onRemove}
          aria-label="Remove test"
          title="Remove test"
          disabled={disabled}
        >
          <CloseGlyph />
        </button>
      </div>

      {!collapsed && (
        <div className="li-dm-test-body">
          <div className="li-dm-two">
            <div className="li-dm-field">
              <label className="li-dm-lbl">
                Lab test <span className="li-dm-req">*</span>
              </label>
              <SearchSelect
                placeholder={catalogLoading ? "Loading lab tests…" : "Search and select a lab test"}
                value={labTest}
                options={labTestOptions}
                getKey={(o) => o.ltCode}
                getLabel={(o) => o.testName}
                getSubLabel={(o) => o.abbreviatedName}
                onChange={onPickLabTest}
                disabled={disabled || catalogLoading}
              />
            </div>
            <div className="li-dm-field li-dm-field-date">
              <label className="li-dm-lbl">
                Test date <span className="li-dm-req">*</span>
              </label>
              {/* Test date bounds: 1900-01-01 → today. Pre-1900 dates
                  are almost always a typo (misplaced year) and would
                  break the trend / audit charts; future dates are
                  never valid. Both `min` and `max` cap the native
                  picker; browsers ignore both for direct keyboard
                  typing, so the clamp runs on BLUR — not onChange.
                  Running it on every keystroke clobbered the reviewer
                  as they typed the year: a native date input fires
                  change after each digit with the year zero-padded
                  (e.g. the first "2" of "2026" surfaces as year
                  0002), which the clamp rewrote to 1900-01-01 and
                  the field then stuck at 1900. Blur-time clamp
                  accepts the full typed value and only validates
                  once the reviewer leaves the field. */}
              <input
                type="date"
                className="li-dm-input"
                placeholder="dd-mm-yyyy"
                value={testDate}
                min="1900-01-01"
                max={todayISO()}
                onChange={(e) => onUpdate({ testDate: e.target.value })}
                onBlur={(e) => {
                  const v = e.target.value;
                  if (!v) return;
                  let clamped = v;
                  if (v > todayISO()) clamped = todayISO();
                  else if (v < "1900-01-01") clamped = "1900-01-01";
                  if (clamped !== v) onUpdate({ testDate: clamped });
                }}
                disabled={disabled}
              />
            </div>
          </div>

          {labTest && subTests.map((s, subIdx) => (
            <SubTestCard
              key={s.id}
              index={subIdx + 1}
              sub={s}
              parentTest={labTest}
              hasSubTestCorpus={hasSubTestCorpus}
              pickedSltCodes={pickedSltCodes}
              unitOptions={unitOptions}
              onRemove={() => onRemoveSub(s.id)}
              onUpdate={(patch) => onUpdateSub(s.id, patch)}
              disabled={disabled}
            />
          ))}

          {labTest && hasSubTestCorpus && (() => {
            const addSubHint = hasBlankSubTest
              ? "Pick a sub test in the current row first"
              : allSubTestsPicked
              ? "All sub tests for this lab test are added"
              : "";
            return (
              <span
                className={`li-dm-add-sub-wrap${addSubHint ? " li-dm-tt" : ""}`}
                data-tt={addSubHint || undefined}
              >
                <button
                  type="button"
                  className="li-dm-add-sub"
                  onClick={onAddSub}
                  disabled={disabled || hasBlankSubTest || allSubTestsPicked}
                >
                  <PlusIcon /> Add sub test
                </button>
              </span>
            );
          })()}
        </div>
      )}
    </div>
  );
};
TestCard.propTypes = {
  index: PropTypes.number.isRequired,
  test: PropTypes.object.isRequired,
  catalog: PropTypes.array.isRequired,
  catalogLoading: PropTypes.bool,
  unitOptions: PropTypes.array,
  pickedLtCodes: PropTypes.instanceOf(Set).isRequired,
  onUpdate: PropTypes.func.isRequired,
  onPickLabTest: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
  onAddSub: PropTypes.func.isRequired,
  onRemoveSub: PropTypes.func.isRequired,
  onUpdateSub: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
};

// ─── SubTestCard ────────────────────────────────────────────────────────
export const SubTestCard = ({
  index,
  sub,
  parentTest,
  hasSubTestCorpus,
  pickedSltCodes,
  unitOptions,
  onRemove,
  onUpdate,
  disabled,
}) => {
  // LOINC display: for parents WITH a subtest corpus we show the fetched
  // long-form `loincName` (from getSubTestOnSelect); while it's in flight
  // show "Loading…" so the reviewer knows something's coming. Falls back
  // to the sub-test's raw `loincCode` if the fetch failed. Parents WITHOUT
  // a subtest corpus fall back to the parent test's own `loincCode`
  // (the parent has no equivalent on-select endpoint on this route).
  const loincName = hasSubTestCorpus
    ? (sub.loincLoading
        ? "Loading…"
        : sub.loincName || (sub.sub && sub.sub.loincCode) || "")
    : (parentTest && parentTest.loincCode) || "";

  const subOptions = useMemo(() => {
    if (!hasSubTestCorpus) return [];
    return parentTest.subtest.filter((o) =>
      !pickedSltCodes.has(o.sltCode) || (sub.sub && sub.sub.sltCode === o.sltCode),
    );
  }, [hasSubTestCorpus, parentTest, pickedSltCodes, sub.sub]);

  const pickSubTest = (picked) => {
    // Stash the pick immediately + reset the LOINC display to a loading
    // state so the reviewer sees the pick land straight away. Tag also
    // resets because the qualifier list is per-sltCode — a tag valid
    // for the previous pick is almost never valid for a new one.
    onUpdate({ sub: picked, loincName: "", loincLoading: true, tag: "" });
    if (!picked || !parentTest || !parentTest.ltCode) {
      onUpdate({ loincLoading: false });
      return;
    }
    new LabTestCatalogService().getSubTestOnSelect(
      parentTest.ltCode,
      picked.sltCode,
      (res) => {
        onUpdate({ loincName: (res && res.loincName) || "", loincLoading: false });
      },
      () => onUpdate({ loincLoading: false }),
    );
  };

  return (
    <div className="li-dm-sub">
      <div className="li-dm-sub-head">
        <span className="li-dm-sub-title">
          Sub test {index} <span className="li-dm-req">*</span>
        </span>
        {hasSubTestCorpus && (
          <button
            type="button"
            className="li-dm-icon-btn"
            onClick={onRemove}
            aria-label="Remove sub test"
            title="Remove sub test"
            disabled={disabled}
          >
            <CloseGlyph />
          </button>
        )}
      </div>
      {hasSubTestCorpus && (
        <div className="li-dm-field">
          <SearchSelect
            placeholder="Search and select a sub test"
            value={sub.sub}
            options={subOptions}
            getKey={(o) => o.sltCode}
            getLabel={(o) => o.subtestName}
            getSubLabel={(o) => o.abbreviatedName}
            onChange={pickSubTest}
            disabled={disabled}
          />
        </div>
      )}
      <div className="li-dm-sub-grid">
        <div
          className={`li-dm-field li-dm-field-loinc${loincName && !sub.loincLoading ? " li-dm-tt" : ""}`}
          data-tt={loincName && !sub.loincLoading ? loincName : undefined}
        >
          <label className="li-dm-lbl">LOINC name</label>
          {/* Full LOINC label revealed on hover via the shared
              .li-dm-tt CSS tooltip (white bg + black text). Native
              `title` intentionally omitted — dual tooltips render as
              two competing popovers. Skipped while the fetch is in
              flight ("Loading…" isn't useful copy to reveal). */}
          <input
            type="text"
            className="li-dm-input is-readonly"
            value={loincName}
            readOnly
            disabled={disabled}
          />
        </div>
        <div className="li-dm-field">
          <label className="li-dm-lbl">
            Value <span className="li-dm-req">*</span>
          </label>
          {/* Values are catalog readings, not free-form notes — clamp to
              100 characters everywhere so the trend UI + LI table can
              rely on a bounded width. `maxLength` covers the keyboard
              path; a defensive slice on onChange stops paste from
              blowing past the ceiling in browsers that don't enforce
              the attribute. */}
          <input
            type="text"
            className="li-dm-input"
            value={sub.value}
            maxLength={100}
            onChange={(e) => onUpdate({ value: (e.target.value || "").slice(0, 100) })}
            disabled={disabled}
          />
        </div>
        <div className="li-dm-field">
          <label className="li-dm-lbl">
            Unit <span className="li-dm-req">*</span>
          </label>
          <div className="li-dm-select-wrap">
            <select
              className="li-dm-select"
              value={sub.unit}
              onChange={(e) => onUpdate({ unit: e.target.value })}
              disabled={disabled}
            >
              <option value="">Select</option>
              {(unitOptions || []).map((u) => (
                <option key={u} value={u}>{u}</option>
              ))}
            </select>
            <span className="li-dm-chev" aria-hidden="true">&#8964;</span>
          </div>
        </div>
        <div className="li-dm-field">
          <label className="li-dm-lbl">
            Range <span className="li-dm-req">*</span>
          </label>
          <div className="li-dm-select-wrap">
            <select
              className="li-dm-select"
              value={sub.range}
              onChange={(e) => onUpdate({ range: e.target.value })}
              disabled={disabled}
            >
              <option value="">Select</option>
              {RANGE_OPTIONS.map((r) => (
                <option key={r.value} value={r.value}>{r.label}</option>
              ))}
            </select>
            <span className="li-dm-chev" aria-hidden="true">&#8964;</span>
          </div>
        </div>
        {(() => {
          // Tag dropdown surfaces only when the picked sub-test's
          // sltCode has a curated tag list (see subtestTags catalog).
          // Hidden otherwise so common sub-tests keep the same
          // Value / Unit / Range shape as before.
          const sltCode = sub.sub && sub.sub.sltCode;
          const tagOpts = sltCode ? getTagsForSltCode(sltCode) : [];
          if (!tagOpts.length) return null;
          return (
            <div className="li-dm-field">
              <label className="li-dm-lbl">
                Tag <span className="li-dm-req">*</span>
              </label>
              <div className="li-dm-select-wrap">
                <select
                  className="li-dm-select"
                  value={sub.tag || ""}
                  onChange={(e) => onUpdate({ tag: e.target.value })}
                  disabled={disabled}
                >
                  <option value="">Select</option>
                  {tagOpts.map((t) => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </select>
                <span className="li-dm-chev" aria-hidden="true">&#8964;</span>
              </div>
            </div>
          );
        })()}
      </div>

      {/* Description (ZY-10340) — one per sub-test, spans the full width
          below the sub-grid. Optional per label + no length cap. The
          card-level canSubmit gate refuses to enable Save unless every
          sub-test has EITHER a Value or a Description (or both);
          backend enforces the same rule via ValidateValueOrDescription. */}
      <div className="li-dm-field li-dm-field-desc">
        <label className="li-dm-lbl">
          Description <span className="li-dm-lbl-hint">&middot; optional</span>
        </label>
        <textarea
          className="li-dm-input li-dm-textarea"
          value={sub.description || ""}
          onChange={(e) => onUpdate({ description: e.target.value })}
          placeholder="Add description…"
          rows={2}
          disabled={disabled}
        />
      </div>
    </div>
  );
};
SubTestCard.propTypes = {
  index: PropTypes.number.isRequired,
  sub: PropTypes.object.isRequired,
  parentTest: PropTypes.object.isRequired,
  hasSubTestCorpus: PropTypes.bool,
  pickedSltCodes: PropTypes.instanceOf(Set).isRequired,
  unitOptions: PropTypes.array,
  onRemove: PropTypes.func.isRequired,
  onUpdate: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
};

// ─── SearchSelect ───────────────────────────────────────────────────────
// Fuzzy-search dropdown backed by a fixed options array. Kept local to
// this modal because the constraints (dedup lists, alphabetical order,
// controlled by parent) don't map onto the existing Select component.
const SearchSelect = ({
  placeholder,
  value,
  options,
  getKey,
  getLabel,
  getSubLabel,
  onChange,
  disabled,
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const boxRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) => {
      const label = String(getLabel(o) || "").toLowerCase();
      const sub = String((getSubLabel && getSubLabel(o)) || "").toLowerCase();
      return label.includes(q) || sub.includes(q);
    });
  }, [query, options, getLabel, getSubLabel]);

  const displayText = value ? getLabel(value) : "";
  // Tooltip on the selected pick — shows the full label on hover so
  // long test names / sub-test names can be read without expanding
  // the input. Only renders once a value is picked AND the picker
  // isn't currently open (open state overrides the display with the
  // reviewer's typed query, tooltip would be misleading).
  const showTooltip = !!(value && displayText && !open);

  return (
    <div
      className={`li-dm-search-wrap${showTooltip ? " li-dm-tt" : ""}`}
      data-tt={showTooltip ? displayText : undefined}
      ref={boxRef}
    >
      <SearchGlyph />
      {/* Native `title` intentionally omitted — the wrapper carries
          the styled .li-dm-tt tooltip; dual tooltips would render as
          two competing popovers. */}
      <input
        type="text"
        className="li-dm-input li-dm-search"
        placeholder={placeholder}
        value={open ? query : displayText}
        onFocus={() => !disabled && setOpen(true)}
        onChange={(e) => { setOpen(true); setQuery(e.target.value); }}
        disabled={disabled}
      />
      <ChevronIcon direction="down" />
      {open && !disabled && (
        <ul className="li-dm-listbox" role="listbox">
          {filtered.length === 0 && (
            <li className="li-dm-list-empty">No matches</li>
          )}
          {filtered.map((o) => {
            const k = getKey(o);
            const isSelected = value && getKey(value) === k;
            return (
              <li
                key={k}
                role="option"
                aria-selected={isSelected}
                className={`li-dm-list-item${isSelected ? " is-on" : ""}`}
                onClick={() => { onChange(o); setOpen(false); setQuery(""); }}
              >
                <span className="li-dm-list-lbl">{getLabel(o)}</span>
                {getSubLabel && getSubLabel(o) && (
                  <span className="li-dm-list-sub">{getSubLabel(o)}</span>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};
SearchSelect.propTypes = {
  placeholder: PropTypes.string,
  value: PropTypes.object,
  options: PropTypes.array.isRequired,
  getKey: PropTypes.func.isRequired,
  getLabel: PropTypes.func.isRequired,
  getSubLabel: PropTypes.func,
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
};

// ─── Tiny icons ─────────────────────────────────────────────────────────
export const PlusIcon = () => (
  <svg viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
    <path d="M6 1v10M1 6h10" strokeLinecap="round" />
  </svg>
);
const SearchGlyph = () => (
  <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4" aria-hidden="true" className="li-dm-search-icon">
    <circle cx="7" cy="7" r="5" />
    <path d="M11 11l3 3" strokeLinecap="round" />
  </svg>
);
const ChevronIcon = ({ direction }) => (
  <svg
    viewBox="0 0 10 6"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.4"
    aria-hidden="true"
    className={`li-dm-chev-icon is-${direction}`}
  >
    <path d="M1 1l4 4 4-4" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);
ChevronIcon.propTypes = { direction: PropTypes.oneOf(["up", "down"]).isRequired };
